import {type PreloadedQuery, readInlineData, usePreloadedQuery, useQueryLoader} from 'react-relay'
import TemplatePickerRepositoryTemplatesRequest, {
  type RepositoryAndTemplatePickerDialogQuery,
} from './__generated__/RepositoryAndTemplatePickerDialogQuery.graphql'
import {RepositoryTemplates} from './RepositoryAndTemplatePickerDialog'
import {
  RepositoryIssueTemplatesFragment,
  type RepositoryPickerRepositoryIssueTemplates$key,
  type RepositoryPickerRepositoryIssueTemplates$data as Templates,
  type RepositoryPickerRepository$data as Repository,
} from '@github-ui/item-picker/repository'
import {useEffect} from 'react'
import {NoTemplates, TemplateList, TemplateListLoading, type TemplateListSelectedProp} from './TemplateList'
import {Box} from '@primer/react'
import {TemplateListPaneFooter} from './TemplateListPaneFooter'
import {InfoIcon} from '@primer/octicons-react'
import {LABELS} from './constants'

export type TemplateListVariant = 'full' | 'large'

type TemplateListPaneProps = {
  templates?: Templates | null
} & TemplateListPaneSharedProps

type TemplateListPaneFromQueryProps = {
  queryRef: PreloadedQuery<RepositoryAndTemplatePickerDialogQuery>
} & TemplateListPaneSharedProps

type TemplateListPaneInternalProps = {
  templates: Templates
} & TemplateListPaneSharedProps

type TemplateListPaneSharedProps = {
  repository: Repository
  /** Id to use on validation messages
   *
   * `TemplateListPane` might be used with a repository that has issues disabled or has no templates available.
   * When this is the case, a message is shown and rendered with the id passed in this property.
   *
   * Consumers can then use this id in conjuction with an `aria-describedby` to ensure their control is properly
   * described with the message and accessible.
   */
  descriptionId?: string
} & TemplateListSelectedProp

export function TemplateListPane({templates, ...props}: TemplateListPaneProps) {
  if (templates) {
    return <TemplateListInternal templates={templates} {...props} />
  }

  return <TemplateListPaneFromRepo {...props} />
}

function TemplateListPaneFromRepo({repository, ...props}: TemplateListPaneProps) {
  const [queryRef, loadTemplates] = useQueryLoader<RepositoryAndTemplatePickerDialogQuery>(
    TemplatePickerRepositoryTemplatesRequest,
  )

  useEffect(() => {
    if (repository) {
      loadTemplates({id: repository.id})
    }
  }, [loadTemplates, repository])

  if (!queryRef) return <TemplateListLoading />

  return <TemplateListPaneFromQuery repository={repository} queryRef={queryRef} {...props} />
}

function TemplateListPaneFromQuery({queryRef, ...props}: TemplateListPaneFromQueryProps) {
  const preloadedData = usePreloadedQuery<RepositoryAndTemplatePickerDialogQuery>(RepositoryTemplates, queryRef)

  // eslint-disable-next-line no-restricted-syntax
  const templates = readInlineData<RepositoryPickerRepositoryIssueTemplates$key>(
    RepositoryIssueTemplatesFragment,
    preloadedData.node,
  )

  if (!templates) {
    return <NoTemplates id={props.descriptionId} />
  }

  return <TemplateListInternal templates={templates} {...props} />
}

function TemplateListInternal({repository, templates, descriptionId, ...props}: TemplateListPaneInternalProps) {
  return (
    <Box sx={{flex: 1, overflowY: 'auto'}}>
      {!repository.hasIssuesEnabled && (
        <Box sx={{display: 'flex', flexDirection: 'row', gap: 2, mt: 2}}>
          <InfoIcon size={24} />
          <span id={descriptionId}>{LABELS.issuesDisabledForRepo}</span>
        </Box>
      )}
      {repository.hasIssuesEnabled && (
        <>
          <TemplateList
            templates={templates}
            {...props}
            sx={{border: '1px solid', borderRadius: 2, borderColor: 'border.muted', py: 0, px: 0}}
          />
          <TemplateListPaneFooter repository={repository} templates={templates} />
        </>
      )}
    </Box>
  )
}

try{ TemplateListPane.displayName ||= 'TemplateListPane' } catch {}
try{ TemplateListPaneFromRepo.displayName ||= 'TemplateListPaneFromRepo' } catch {}
try{ TemplateListPaneFromQuery.displayName ||= 'TemplateListPaneFromQuery' } catch {}
try{ TemplateListInternal.displayName ||= 'TemplateListInternal' } catch {}